import { useState, useEffect } from "react";
import apiClient from "../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, Lightning, CheckCircle, Warning } from "@phosphor-icons/react";

export default function PendingActivationsCard() {
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [activatable, setActivatable] = useState(0);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [actionId, setActionId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/check-pending-activations");
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setActivatable(r.data.activatable || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando lista");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const runSafetyNet = async () => {
    setScanning(true);
    try {
      await apiClient.post("/admin/run-safety-net-now");
      toast.success("✅ Escaneo lanzado. Resultados en 30s — recargando...");
      setTimeout(load, 30000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error lanzando scan");
    }
    setScanning(false);
  };

  const rescue = async (email) => {
    setActionId(email);
    try {
      const r = await apiClient.post("/admin/trial/rescue-by-email", { email });
      toast.success(`✅ ${email} activado — vence ${r.data.expires_at?.slice(0, 10) || "?"}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo activar");
    }
    setActionId(null);
  };

  const containerColor = activatable > 0 ? "rgba(239,68,68,0.06)" : total > 0 ? "rgba(212,175,55,0.04)" : "rgba(16,185,129,0.04)";
  const borderColor    = activatable > 0 ? "rgba(239,68,68,0.3)"  : total > 0 ? "rgba(212,175,55,0.2)"  : "rgba(16,185,129,0.2)";
  const accent         = activatable > 0 ? "#F87171" : total > 0 ? "#D4AF37" : "#10B981";

  return (
    <div
      data-testid="pending-activations-card"
      className="rounded-xl p-5 border"
      style={{ background: containerColor, borderColor }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold mb-1" style={{ color: accent }}>
            🛡️ Stripe Safety-Net
          </div>
          <div className="text-sm font-semibold text-white">
            Activaciones pendientes — pagaron pero no están activos
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            El sistema escanea Stripe automáticamente cada <strong>5 minutos</strong> y activa cuentas que pagaron sin que el webhook llegue. Aquí ves los pendientes ahora.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="safety-net-refresh-btn"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-xs font-semibold disabled:opacity-50"
            style={{ borderColor, color: accent, background: containerColor }}
          >
            <ArrowsClockwise className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Recargar
          </button>
          <button
            data-testid="safety-net-run-now-btn"
            onClick={runSafetyNet}
            disabled={scanning}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all disabled:opacity-50"
            style={{
              borderColor,
              background: activatable > 0 ? "rgba(239,68,68,0.15)" : "rgba(212,175,55,0.15)",
              color: accent,
              border: `1px solid ${borderColor}`,
            }}
          >
            <Lightning weight="fill" className="w-3.5 h-3.5" />
            {scanning ? "Escaneando…" : "Escanear ahora"}
          </button>
        </div>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label="Total pendientes" value={total} color="#fff" />
        <Stat label="Activables" value={activatable} color={activatable > 0 ? "#F87171" : "#10B981"} />
        <Stat label="No activables" value={total - activatable} color="#9CA3AF" />
      </div>

      {/* List */}
      {loading && items.length === 0 ? (
        <div className="text-xs text-zinc-500 text-center py-4">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-4 text-xs" style={{ color: "#10B981" }}>
          <CheckCircle weight="fill" className="w-4 h-4" />
          Todos los pagos están reflejados correctamente — ningún usuario atorado.
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {items.map((it) => (
            <div
              key={it.id}
              className="rounded-lg p-3 border flex items-center justify-between gap-3"
              style={{
                background: "rgba(255,255,255,0.02)",
                borderColor: it.can_be_activated ? "rgba(239,68,68,0.3)" : "rgba(255,255,255,0.06)",
              }}
              data-testid={`pending-row-${it.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-white truncate">{it.email}</div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {it.name || "Sin nombre"} · creado {it.created_at?.slice(0, 16)}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-[10px]">
                  <Badge label={`App: ${it.subscription_status || "—"}`} color={it.subscription_status === "inactive" ? "#F87171" : "#9CA3AF"} />
                  <Badge label={`Stripe: ${it.stripe_status || "sin sub"}`}
                         color={it.stripe_status === "trialing" || it.stripe_status === "active" ? "#10B981" : "#9CA3AF"} />
                  {it.reason && (
                    <span className="text-[10px] text-zinc-500 italic flex items-center gap-1">
                      <Warning className="w-3 h-3" /> {it.reason}
                    </span>
                  )}
                </div>
              </div>
              {it.can_be_activated ? (
                <button
                  data-testid={`activate-now-${it.id}`}
                  onClick={() => rescue(it.email)}
                  disabled={actionId === it.email}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all disabled:opacity-50"
                  style={{
                    background: "rgba(16,185,129,0.18)",
                    color: "#10B981",
                    border: "1px solid rgba(16,185,129,0.4)",
                  }}
                >
                  {actionId === it.email ? "..." : "Activar ahora"}
                </button>
              ) : (
                <span className="text-[10px] text-zinc-600 italic flex-shrink-0">
                  No activable
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-lg p-3 text-center" style={{ background: "rgba(255,255,255,0.03)" }}>
      <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "#9CA3AF" }}>
        {label}
      </div>
      <div className="text-2xl font-black tabular-nums" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function Badge({ label, color }) {
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-[10px] font-bold"
      style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
    >
      {label}
    </span>
  );
}
